import { useState, useCallback } from 'react';
import { useToastNotifications } from '@/lib/hooks/use-toast-notifications';
import { useI18n } from '@/i18n/I18nProvider';
import { authenticatedFetcher } from '@/lib/swr';
import { downloadBase64File } from '@/lib/helpers';
import type { ApiResponse } from '@/types/api-response';

interface AttachmentDownloadResponseDto {
  fileName?: string | null;
  contentType?: string | null;
  base64Content?: string | null;
}

export function useDownloadAttachment() {
  const [isDownloading, setIsDownloading] = useState(false);
  const toast = useToastNotifications();
  const { t } = useI18n();

  const downloadAttachment = useCallback(
    async (url: string) => {
      if (!url) return;

      setIsDownloading(true);

      try {
        const res = await authenticatedFetcher<ApiResponse<AttachmentDownloadResponseDto>>(url);
        const { fileName, contentType, base64Content } = res.data || {};

        if (!base64Content) {
          toast.error(t.errorOccurred, t.failedToDownloadAttachment);
          return;
        }

        downloadBase64File(base64Content, fileName!, contentType!);

        toast.success(t.downloaded, t.fileDownloadedSuccessfully);
      } catch (err) {
        console.error('Failed to download attachment:', err);
        toast.error(t.errorOccurred, t.failedToDownloadAttachment);
      } finally {
        setIsDownloading(false);
      }
    },
    [toast, t]
  );

  return {
    downloadAttachment,
    isDownloading,
  };
}
